import { useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MousePointer2, Type, Timer, Table2, Trash2, Pencil, Calendar, ArrowUp, ArrowDown, Plus, Globe } from "lucide-react";

const META = {
  click:    { icon: MousePointer2, label: 'Clicar',    color: 'bg-[hsl(var(--step-click))]/15 text-[hsl(var(--step-click))] border-[hsl(var(--step-click))]/30' },
  fill:     { icon: Type,          label: 'Preencher',     color: 'bg-[hsl(var(--step-fill))]/15 text-[hsl(var(--step-fill))] border-[hsl(var(--step-fill))]/30' },
  wait:     { icon: Timer,         label: 'Esperar',     color: 'bg-[hsl(var(--step-wait))]/15 text-[hsl(var(--step-wait))] border-[hsl(var(--step-wait))]/30' },
  extract:  { icon: Table2,        label: 'Extrair',  color: 'bg-[hsl(var(--step-extract))]/15 text-[hsl(var(--step-extract))] border-[hsl(var(--step-extract))]/30' },
  navigate: { icon: Globe,         label: 'Navegar', color: 'bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/30' },
};

const DATE_FORMATS = [
  { id: 'DD/MM/YYYY', label: 'DD/MM/YYYY (ex: 11/05/2025)' },
  { id: 'DD-MM-YYYY', label: 'DD-MM-YYYY (ex: 11-05-2025)' },
  { id: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ex: 2025-05-11)' },
  { id: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
  { id: 'DD/MM/YY',   label: 'DD/MM/YY' },
];

function StepEditor({ step, onChange }) {
  const isFill = step.type === 'fill';
  const isClick = step.type === 'click';
  const isExtract = step.type === 'extract';
  const isWait = step.type === 'wait';
  const isNavigate = step.type === 'navigate';
  return (
    <div className="space-y-3 w-80">
      {isNavigate && (
        <div>
          <Label className="text-xs">URL alvo</Label>
          <Input
            data-testid="step-editor-url"
            value={step.url || ''}
            onChange={(e) => onChange({ ...step, url: e.target.value })}
            className="mono"
            placeholder='https://exemplo.com/lista?data_inicio=${DATE_START:DD/MM/YYYY}&data_fim=${DATE_END:DD/MM/YYYY}'
          />
          <div className="text-[11px] text-muted-foreground mt-1">
            Suporta placeholders <span className="mono">{"${DATE_START:DD/MM/YYYY}"}</span>, <span className="mono">{"${DATE_END:DD/MM/YYYY}"}</span> e <span className="mono">{"${DATE:fmt}"}</span> (modo "Dias" usa este último).
          </div>
        </div>
      )}
      {(isFill || isClick || isExtract) && (
        <div>
          <Label className="text-xs">Selector CSS</Label>
          <Input
            data-testid="step-editor-selector"
            value={step.selector || ''}
            onChange={(e) => onChange({ ...step, selector: e.target.value })}
            className="mono"
            placeholder='ex: input[name="email"] ou #botao'
          />
        </div>
      )}
      <div>
        <Label className="text-xs">Nota (hint)</Label>
        <Input
          data-testid="step-editor-hint"
          value={step.hint || ''}
          onChange={(e) => onChange({ ...step, hint: e.target.value })}
          placeholder="descrição opcional"
        />
      </div>
      {isFill && (
        <>
          <div>
            <Label className="text-xs">Valor a digitar</Label>
            <Input data-testid="step-editor-value" value={step.value || ''} onChange={(e) => onChange({ ...step, value: e.target.value })} className="mono" placeholder="valor…" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm">Campo de data</Label>
              <p className="text-xs text-muted-foreground">Substitui este valor pela data alvo quando corres por intervalo ou por dias específicos.</p>
            </div>
            <Switch checked={!!step.is_date} onCheckedChange={(v) => onChange({ ...step, is_date: v })} data-testid="step-editor-is-date-switch" />
          </div>
          {step.is_date && (
            <>
              <div>
                <Label className="text-xs">Formato de data</Label>
                <Select value={step.date_format || 'DD/MM/YYYY'} onValueChange={(v) => onChange({ ...step, date_format: v })}>
                  <SelectTrigger data-testid="step-editor-date-format-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DATE_FORMATS.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Papel desta data (modo "Intervalo")</Label>
                <Select value={step.date_role || 'start'} onValueChange={(v) => onChange({ ...step, date_role: v })}>
                  <SelectTrigger data-testid="step-editor-date-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="start">Data início (start)</SelectItem>
                    <SelectItem value="end">Data fim (end)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">No modo "Dias" ambos os papéis recebem o mesmo dia.</p>
              </div>
            </>
          )}
          <div className="text-[11px] text-muted-foreground">Dica: também podes usar <span className="mono">{'${DATE:DD/MM/YYYY}'}</span>, <span className="mono">{'${DATE_START:DD/MM/YYYY}'}</span> ou <span className="mono">{'${DATE_END:DD/MM/YYYY}'}</span> dentro do valor.</div>
        </>
      )}
      {isWait && (
        <div>
          <Label className="text-xs">Esperar (ms)</Label>
          <Input data-testid="step-editor-wait-ms" type="number" min={100} max={60000} step={100} value={step.ms || 1000} onChange={(e) => onChange({ ...step, ms: Number(e.target.value) })} />
        </div>
      )}
    </div>
  );
}

function AddStepDialog({ open, onOpenChange, onAdd }) {
  const [type, setType] = useState('click');
  const [selector, setSelector] = useState('');
  const [value, setValue] = useState('');
  const [url, setUrl] = useState('');
  const [ms, setMs] = useState(1000);
  const [hint, setHint] = useState('');

  function reset() { setType('click'); setSelector(''); setValue(''); setUrl(''); setMs(1000); setHint(''); }

  function handleAdd() {
    const id = `m-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const base = { id, type, hint: hint || undefined };
    let step;
    if (type === 'wait') step = { ...base, ms: Number(ms) || 1000 };
    else if (type === 'extract') step = { ...base, selector: selector || undefined };
    else if (type === 'navigate') step = { ...base, url: url || '' };
    else step = { ...base, selector: selector || '', ...(type === 'fill' ? { value } : {}) };
    onAdd?.(step);
    reset();
    onOpenChange?.(false);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange?.(o); }}>
      <DialogContent data-testid="add-step-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Adicionar passo manualmente</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Tipo</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="add-step-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="click">Clicar</SelectItem>
                <SelectItem value="fill">Preencher (campo)</SelectItem>
                <SelectItem value="wait">Esperar (ms)</SelectItem>
                <SelectItem value="extract">Extrair (tabela)</SelectItem>
                <SelectItem value="navigate">Navegar (ir para URL)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(type === 'click' || type === 'fill' || type === 'extract') && (
            <div>
              <Label className="text-xs">Selector CSS {type === 'extract' ? '(opcional)' : ''}</Label>
              <Input data-testid="add-step-selector" value={selector} onChange={(e) => setSelector(e.target.value)} className="mono" placeholder='ex: #login-email · a[href*="maquina=1594"]' />
            </div>
          )}
          {type === 'fill' && (
            <div>
              <Label className="text-xs">Valor</Label>
              <Input data-testid="add-step-value" value={value} onChange={(e) => setValue(e.target.value)} className="mono" placeholder="texto a digitar" />
            </div>
          )}
          {type === 'navigate' && (
            <div>
              <Label className="text-xs">URL alvo</Label>
              <Input data-testid="add-step-url" value={url} onChange={(e) => setUrl(e.target.value)} className="mono" placeholder='https://exemplo.com/?data_inicio=${DATE_START:DD/MM/YYYY}&data_fim=${DATE_END:DD/MM/YYYY}' />
              <p className="text-[11px] text-muted-foreground mt-1">Aceita placeholders <span className="mono">{"${DATE_START}"}</span>, <span className="mono">{"${DATE_END}"}</span>, <span className="mono">{"${DATE}"}</span>.</p>
            </div>
          )}
          {type === 'wait' && (
            <div>
              <Label className="text-xs">Esperar (ms)</Label>
              <Input data-testid="add-step-ms" type="number" min={100} max={60000} step={100} value={ms} onChange={(e) => setMs(Number(e.target.value))} />
            </div>
          )}
          <div>
            <Label className="text-xs">Nota (hint, opcional)</Label>
            <Input data-testid="add-step-hint" value={hint} onChange={(e) => setHint(e.target.value)} placeholder="descrição curta" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange?.(false)} data-testid="add-step-cancel">Cancelar</Button>
          <Button onClick={handleAdd} data-testid="add-step-confirm" disabled={(type === 'navigate' && !url.trim()) || (type !== 'wait' && type !== 'extract' && type !== 'navigate' && !selector.trim())}>Adicionar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function StepsTimeline({ steps = [], onUpdate, onDelete, onDeleteLast, onMove, onAdd }) {
  const [addOpen, setAddOpen] = useState(false);
  function update(idx, newStep) { onUpdate?.(idx, newStep); }

  return (
    <Card data-testid="steps-timeline" className="flex flex-col h-full min-h-[400px]">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div>
          <div className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Passos gravados</div>
          <div className="text-xs text-muted-foreground">{steps.length} passo{steps.length === 1 ? '' : 's'} capturado{steps.length === 1 ? '' : 's'}</div>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="outline" onClick={() => setAddOpen(true)} data-testid="steps-timeline-add-button" className="gap-1">
            <Plus className="h-3.5 w-3.5" /> Adicionar
          </Button>
          <Button size="sm" variant="ghost" onClick={onDeleteLast} disabled={!steps.length} data-testid="steps-timeline-delete-last-button">
            Anular último
          </Button>
        </div>
      </div>
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-2">
          {steps.length === 0 && (
            <div className="text-xs text-muted-foreground text-center py-8">
              Sem passos ainda. Clica no screenshot para gravar ações ou usa <strong>Adicionar</strong>.
            </div>
          )}
          {steps.map((s, idx) => {
            const m = META[s.type] || META.click;
            const Icon = m.icon;
            return (
              <div key={s.id || idx} data-testid="steps-timeline-step-row" className="rounded-lg border border-border bg-card hover:bg-muted/40 transition-colors duration-150 px-3 py-2.5">
                <div className="flex items-start gap-3">
                  <div className="h-6 w-6 rounded-full bg-muted text-foreground grid place-items-center text-[11px] font-semibold flex-shrink-0">{idx + 1}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={`gap-1 ${m.color}`}>
                        <Icon className="h-3 w-3" /> {m.label}
                      </Badge>
                      {s.is_date && (
                        <Badge variant="outline" className="gap-1 bg-[hsl(var(--info))]/15 text-[hsl(var(--info))] border-[hsl(var(--info))]/30" data-testid="step-is-date-badge">
                          <Calendar className="h-3 w-3" /> Data ({s.date_format || 'DD/MM/YYYY'})
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground truncate">{s.hint || ''}</span>
                    </div>
                    {s.selector && (
                      <div className="mono text-[11px] text-muted-foreground truncate mt-1">{s.selector}</div>
                    )}
                    {s.type === 'fill' && (
                      <div className="text-[11px] text-muted-foreground mt-1">valor: <span className="mono">{s.value}</span></div>
                    )}
                    {s.type === 'wait' && (
                      <div className="text-[11px] text-muted-foreground mt-1">{s.ms}ms</div>
                    )}
                  </div>
                  <div className="flex items-center gap-0.5">
                    <button
                      onClick={() => onMove?.(idx, -1)}
                      disabled={idx === 0}
                      title="Subir"
                      className="text-muted-foreground hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed p-1"
                      data-testid={`step-up-button-${idx}`}
                    >
                      <ArrowUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => onMove?.(idx, 1)}
                      disabled={idx === steps.length - 1}
                      title="Descer"
                      className="text-muted-foreground hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed p-1"
                      data-testid={`step-down-button-${idx}`}
                    >
                      <ArrowDown className="h-3.5 w-3.5" />
                    </button>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="text-muted-foreground hover:text-primary p-1" title="Editar" data-testid={`step-edit-button-${idx}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="end" className="w-auto">
                        <StepEditor step={s} onChange={(ns) => update(idx, ns)} />
                      </PopoverContent>
                    </Popover>
                    <button onClick={() => onDelete?.(idx)} title="Apagar" className="text-muted-foreground hover:text-destructive p-1" data-testid={`step-delete-button-${idx}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
      <AddStepDialog open={addOpen} onOpenChange={setAddOpen} onAdd={(st) => onAdd?.(st)} />
    </Card>
  );
}
